import { supabase } from "@/integrations/supabase/client";
import type { Contract } from "@/types/contracts";
import type { Reservation } from "@/types/supabase";
import {
  applyContractMergeFields,
  loadContractMergeData,
} from "@/lib/contractMergeFields";


export const getContractsByReservation = async (reservationId: string) => {
  const { data, error } = await supabase
    .from('contracts')
    .select('*')
    .eq('reservation_id', reservationId)
    .order('contract_type', { ascending: true });
    
  if (error) throw error;
  return data as Contract[];
};

export const getContractByToken = async (token: string) => {
  const { data, error } = await supabase
    .from('contracts')
    .select(`
      *,
      reservations:reservation_id (
        first_name,
        last_name,
        email,
        phone,
        check_in,
        check_out,
        guests,
        total_price
      )
    `)
    .eq('unique_token', token)
    .single();
    
  if (error) throw error;
  return data;
};

export const signContract = async (token: string, signatureData: string) => {
  try {
    // Récupérer l'adresse IP du client
    let userIP = 'Non disponible';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      userIP = ipData.ip;
    } catch (ipError) {
      console.warn('Impossible de récupérer l\'adresse IP:', ipError);
    }

    const { data, error } = await supabase
      .from('contracts')
      .update({
        status: 'signed',
        signed_at: new Date().toISOString(),
        signature_data: signatureData,
        signer_ip: userIP
      })
      .eq('unique_token', token)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la signature du contrat:', error);
    throw error;
  }
};

export const downloadContractPDF = async (pdfPath: string) => {
  try {
    const url = `https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/generated-contracts/${pdfPath}`;
    
    // Télécharger le fichier au lieu de l'ouvrir dans un nouvel onglet
    const response = await fetch(url);
    const blob = await response.blob();
    
    // Créer un lien de téléchargement
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = pdfPath.split('/').pop() || 'contrat.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Erreur lors du téléchargement du PDF:', error);
    throw error;
  }
};

export const generateContractContent = (reservation: Reservation, contractType: 'lease' | 'rules', clientData?: { address?: string; city?: string; postal_code?: string; country?: string }) => {
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('fr-FR');
  const formatDateTime = (dateStr: string, time?: string) => {
    const date = new Date(dateStr).toLocaleDateString('fr-FR');
    return time ? `${date} à ${time}` : date;
  };
  const currentDate = new Date().toLocaleDateString('fr-FR');
  const checkInTime = reservation.check_in_time || '16:00';
  const checkOutTime = reservation.check_out_time || '10:00';
  const reservationNumber = reservation.id.slice(0, 8).toUpperCase();
  
  // Client address
  const clientAddress = clientData?.address || '';
  const clientCity = clientData?.city || '';
  const clientPostalCode = clientData?.postal_code || '';
  const clientCountry = clientData?.country || 'France';
  const fullAddress = [clientAddress, clientPostalCode, clientCity, clientCountry].filter(Boolean).join(', ');
  
  if (contractType === 'lease') {
    // Utiliser le nouveau template Villa Douce Vague avec personnalisation
    return `
<div style="font-family: 'Times New Roman', serif; line-height: 1.4; max-width: 800px; margin: 0 auto; padding: 30px; color: #333;">
  <div style="text-align: center; margin-bottom: 40px; border-bottom: 3px solid #0066cc; padding-bottom: 20px;">
    <h1 style="font-size: 22px; font-weight: bold; color: #0066cc; margin: 0 0 10px 0; text-transform: uppercase; letter-spacing: 1px;">
      ENGAGEMENT DE LOCATION MEUBLÉE SAISONNIÈRE
    </h1>
    <h2 style="font-size: 16px; font-style: italic; color: #666; margin: 0; font-weight: normal;">
      SEASONAL RENTAL AGREEMENT FOR FURNISHED ACCOMMODATION
    </h2>
    <p style="margin: 10px 0 0 0; font-size: 14px; color: #666;">Réservation N° ${reservationNumber}</p>
  </div>

  <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #0066cc;">
    <p style="margin: 0 0 15px 0; font-size: 14px; line-height: 1.6;">
      <strong>Le bailleur et le locataire ont convenu et arrêté ce qui suit :</strong><br>
      <em style="color: #666;">The agency and the tenant agreed on :</em>
    </p>
    <p style="margin: 0; font-size: 14px; line-height: 1.6;">
      La SARL ETIEVENT, représentée par MR ETIEVENT GILLES, loue à <strong style="color: #0066cc;">${reservation.first_name} ${reservation.last_name}</strong> qui accepte, à ce titre la location saisonnière du bien ci-après désigné :<br>
      <em style="color: #666;">The SARL ETIEVENT, represented by MR ETIEVENT GILLES, hereby is renting on a seasonal lease to the tenant, who accepts the property described below:</em>
    </p>
  </div>

  <div style="margin-bottom: 25px;">
    <h3 style="background: #0066cc; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
      DÉSIGNATION DU BIEN / DESCRIPTION OF THE PROPERTY
    </h3>
    <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
        <div>
          <p style="margin: 0 0 10px 0;"><strong style="color: #0066cc;">ADRESSE / ADDRESS:</strong></p>
          <div style="background: #f0f9ff; padding: 15px; border-radius: 4px; border-left: 3px solid #0066cc;">
            <p style="margin: 0; font-weight: bold; font-size: 15px;">Villa "Douce Vague"</p>
            <p style="margin: 5px 0 0 0;">97 RUE DES AMANDIERS</p>
            <p style="margin: 5px 0 0 0;">30260 LE GRAU DU ROI</p>
          </div>
        </div>
        <div>
          <p style="margin: 0 0 10px 0;"><strong style="color: #0066cc;">DESCRIPTION:</strong></p>
          <div style="background: #f0f9ff; padding: 15px; border-radius: 4px; border-left: 3px solid #0066cc;">
            <p style="margin: 0;">MAISON d'une surface de <strong>145 m²</strong> environ</p>
            <p style="margin: 5px 0 0 0;">Couchage prévu pour un maximum de <strong>${reservation.guests} personnes</strong></p>
            <p style="margin: 10px 0 0 0; font-style: italic; color: #666; font-size: 13px;">House providing a surface of around 145 sqm with maximum sleeping facilities for ${reservation.guests} persons.</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div style="margin-bottom: 25px;">
    <h3 style="background: #0066cc; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
      CONDITIONS DE LOCATION / RENTAL CONDITIONS
    </h3>
    <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
      <p style="margin: 0 0 15px 0; font-size: 15px;"><strong>La présente location est consentie pour la période :</strong><br>
      <em style="color: #666;">This rental is agreed for the following period :</em></p>
      
      <div style="background: #fef3c7; padding: 15px; border-radius: 6px; border-left: 4px solid #f59e0b; margin-bottom: 20px;">
        <p style="margin: 0 0 8px 0;">• <strong>Du ${formatDate(reservation.check_in)} à partir de ${checkInTime}</strong><br>
        <em style="color: #666; font-size: 13px;">From ${formatDate(reservation.check_in)} from ${checkInTime}</em></p>
        <p style="margin: 0;">• <strong>Au ${formatDate(reservation.check_out)} jusqu'à ${checkOutTime}</strong><br>
        <em style="color: #666; font-size: 13px;">To ${formatDate(reservation.check_out)} until ${checkOutTime}</em></p>
      </div>
    </div>
  </div>

  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 25px; margin-bottom: 25px;">
    <div>
      <h3 style="background: #0066cc; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
        LOCATAIRE / TENANT
      </h3>
      <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
        <p style="margin: 0 0 10px 0;"><strong>Nom Prénom :</strong> ${reservation.first_name} ${reservation.last_name}</p>
        ${fullAddress ? `<p style="margin: 0 0 10px 0;"><strong>Adresse :</strong> ${fullAddress}</p>` : ''}
        <p style="margin: 0 0 10px 0;"><strong>Téléphone :</strong> ${reservation.phone}</p>
        <p style="margin: 0 0 15px 0;"><strong>Email :</strong> ${reservation.email}</p>
        <p style="margin: 0; font-style: italic; color: #666; font-size: 13px;">
          Dénommé "le locataire" d'autre part<br>
          Hereafter called "the tenant" for the other party
        </p>
      </div>
    </div>
    
    <div>
      <h3 style="background: #0066cc; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
        MANDATAIRE
      </h3>
      <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
        <p style="margin: 0 0 5px 0; font-weight: bold;">SARL ETIEVENT</p>
        <p style="margin: 0 0 5px 0;">97 RUE DES AMANDIERS</p>
        <p style="margin: 0 0 10px 0;">30260 LE GRAU DU ROI</p>
        <p style="margin: 0 0 5px 0;"><strong>Téléphone :</strong> 06.21.91.12.34</p>
        <p style="margin: 0 0 15px 0;"><strong>Email :</strong> etieventmarion@gmail.com</p>
        <p style="margin: 0; font-style: italic; color: #666; font-size: 13px;">
          Dénommé "le bailleur" d'une part<br>
          Hereafter called "the landlord" for one party
        </p>
      </div>
    </div>
  </div>

  <div style="margin-bottom: 25px;">
    <h3 style="background: #0066cc; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
      PRIX ET CONDITIONS / PRICE & CONDITIONS
    </h3>
    <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
      <div style="background: #dcfce7; padding: 20px; border-radius: 6px; border-left: 4px solid #16a34a; margin-bottom: 20px; text-align: center;">
        <p style="margin: 0 0 10px 0; font-size: 18px;"><strong style="color: #0066cc;">PRIX TTC / RENTAL PRICE:</strong></p>
        <p style="margin: 0; font-size: 24px; font-weight: bold; color: #16a34a;">${reservation.total_price} €</p>
      </div>
      
      <div style="margin-bottom: 20px;">
        <p style="margin: 0 0 10px 0; font-weight: bold; color: #0066cc;">Ce prix comprend / This price includes :</p>
        <ul style="margin: 0; padding-left: 20px; color: #333;">
          <li>Le linge de lits / Bed linen</li>
          <li>Les serviettes de toilettes / Bath towels</li>
          <li>La taxe de séjour / The local tax</li>
        </ul>
      </div>
      
      <div style="margin-bottom: 20px;">
        <p style="margin: 0 0 10px 0; font-weight: bold; color: #dc2626;">Ce prix ne comprend pas / This price does not include :</p>
        <ul style="margin: 0; padding-left: 20px; color: #333;">
          <li>Communications téléphoniques / Telephone calls</li>
          <li>Les serviettes de piscine ou plage ne sont pas fournies / Pool or beach towels are not provided</li>
        </ul>
      </div>
    </div>
  </div>

  <div style="margin-bottom: 25px;">
    <h3 style="background: #dc2626; color: white; padding: 12px 20px; margin: 0 0 15px 0; border-radius: 6px 6px 0 0; font-size: 16px; font-weight: bold;">
      DÉPÔT DE GARANTIE / SECURITY DEPOSIT
    </h3>
    <div style="border: 1px solid #ddd; border-top: none; padding: 20px; border-radius: 0 0 6px 6px;">
      <div style="background: #fef2f2; padding: 20px; border-radius: 6px; border-left: 4px solid #dc2626; margin-bottom: 15px;">
        <p style="margin: 0 0 10px 0; font-size: 18px; font-weight: bold; color: #dc2626; text-align: center;">2 000 € de caution</p>
        <p style="margin: 0; font-size: 14px; line-height: 1.6;">
          Par chèque au moment de l'arrivée ou par virement avant l'arrivée des locataires.<br>
          <em style="color: #666;">By check on arrival or bank transfer before the tenant's arrival.</em>
        </p>
      </div>
      
      <p style="margin: 0 0 15px 0; font-size: 14px; line-height: 1.6;">
        <strong>Important :</strong> La caution n'est pas encaissée et sera annulée 15 jours après le départ si aucun dommage n'est constaté.<br>
        <em style="color: #666;">The security deposit is not debited from your bank account and will be cancelled 15 days after departure if no damages are reported.</em>
      </p>
      
      <div style="background: #fef3c7; padding: 15px; border-radius: 6px; border-left: 4px solid #f59e0b;">
        <p style="margin: 0; font-size: 13px; line-height: 1.6;">
          <strong>Conditions de ménage :</strong> Le ménage de sortie sera effectué par nos soins. Cependant, nous vous demandons de faire votre vaisselle, vider et laver le réfrigérateur, et descendre vos poubelles. Si ce n'est pas fait, 75€ seront prélevés de la caution.
        </p>
      </div>
    </div>
  </div>

  <div style="margin-top: 40px; padding: 25px; background: #f8fafc; border-radius: 8px; border: 2px solid #0066cc;">
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 40px; text-align: center;">
      <div>
        <p style="margin: 0 0 10px 0; font-weight: bold; color: #0066cc;">SARL ETIEVENT</p>
        <p style="margin: 0 0 20px 0; font-size: 13px; color: #666;">Signature précédée de la mention "Lu et approuvé"</p>
        <div style="border-top: 2px solid #ccc; margin-top: 40px; padding-top: 10px;">
          <p style="margin: 0; font-size: 12px; color: #666;">Le bailleur</p>
        </div>
      </div>
      
      <div>
        <p style="margin: 0 0 10px 0; font-weight: bold; color: #0066cc;">LE LOCATAIRE / THE TENANT</p>
        <p style="margin: 0 0 20px 0; font-size: 13px; color: #666;">Signature précédée de la mention "Lu et approuvé"</p>
        <div style="border-top: 2px solid #ccc; margin-top: 40px; padding-top: 10px;">
          <p style="margin: 0; font-size: 12px; color: #666;">${reservation.first_name} ${reservation.last_name}</p>
        </div>
      </div>
    </div>
    
    <div style="text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd;">
      <p style="margin: 0; font-size: 13px; color: #666;">
        Fait à LE GRAU DU ROI, le ${currentDate}<br>
        <em>Done in LE GRAU DU ROI</em>
      </p>
    </div>
  </div>
</div>
    `;
  } else {
    return `
<div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px;">
  <h1 style="text-align: center; color: #0891b2; margin-bottom: 30px;">RÈGLEMENT INTÉRIEUR</h1>
  <p style="text-align: center; color: #666; margin-bottom: 30px;">Villa Douce Vague</p>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">ARRIVÉE ET DÉPART</h2>
  <div style="background: #f0f9ff; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <ul style="margin: 0;">
      <li><strong>Arrivée :</strong> à partir de 16h00 (remise des clés selon modalités communiquées)</li>
      <li><strong>Départ :</strong> avant 11h00 (restitution des clés)</li>
      <li><strong>Retard :</strong> Merci de nous prévenir en cas de retard</li>
    </ul>
  </div>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">UTILISATION DES ÉQUIPEMENTS</h2>
  <ul style="line-height: 1.8;">
    <li><strong>Piscine :</strong> Accessible de 8h à 22h - Surveillance des enfants obligatoire</li>
    <li><strong>Respect du voisinage :</strong> Pas de bruit excessif après 22h</li>
    <li><strong>Terrasses et jardins :</strong> À votre disposition dans le respect de la tranquillité</li>
    <li><strong>Interdiction de fumer :</strong> À l'intérieur de la villa</li>
  </ul>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">ENTRETIEN ET PROPRETÉ</h2>
  <ul style="line-height: 1.8;">
    <li>Maintenir les lieux propres et rangés pendant le séjour</li>
    <li>Évacuer les déchets dans les contenants prévus à cet effet</li>
    <li>Faire la vaisselle et ranger avant le départ</li>
    <li>Signaler immédiatement tout dysfonctionnement ou problème</li>
  </ul>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">SÉCURITÉ</h2>
  <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <ul style="margin: 0; line-height: 1.8;">
      <li>Fermer portes et fenêtres en cas d'absence</li>
      <li>Ne pas laisser d'objets de valeur à la vue</li>
      <li>Couper l'eau en cas d'absence prolongée</li>
      <li>Respecter les consignes de sécurité piscine</li>
    </ul>
  </div>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">INTERDICTIONS FORMELLES</h2>
  <div style="background: #fee2e2; padding: 15px; border-radius: 8px; border-left: 4px solid #ef4444;">
    <ul style="margin: 0; line-height: 1.8;">
      <li><strong>Fêtes et réceptions :</strong> Non autorisées sans accord préalable</li>
      <li><strong>Animaux :</strong> Interdits sauf accord écrit préalable</li>
      <li><strong>Sous-location :</strong> Strictement interdite</li>
      <li><strong>Nombre de personnes :</strong> Ne pas dépasser la capacité autorisée</li>
    </ul>
  </div>
  
  <h2 style="color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 5px;">CONTACT URGENCE</h2>
  <div style="background: #f0f9ff; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
    <p><strong>En cas de problème urgent :</strong> [NUMÉRO DE TÉLÉPHONE À COMPLÉTER]</p>
    <p><strong>Email :</strong> contact@villadoucevague.fr</p>
  </div>
  
  <div style="margin-top: 40px; padding: 20px; background: #fee2e2; border-radius: 8px; border-left: 4px solid #ef4444;">
    <p style="margin: 0; font-style: italic; text-align: center;">
      <strong>Le non-respect de ce règlement peut entraîner la résiliation immédiate du contrat de location sans remboursement.</strong>
    </p>
  </div>
</div>
    `;
  }
};

/**
 * Build the contract HTML for a reservation.
 * - If a template exists for the given contract type, use its content with merge fields.
 * - Otherwise fall back to the built-in `generateContractContent` output.
 * Returns { content, templateId } so the caller can persist the template lock.
 */
export const buildContractContent = async (
  reservationId: string,
  contractType: 'lease' | 'rules',
  opts?: { templateId?: string | null }
): Promise<{ content: string; templateId: string | null }> => {
  const mergeData = await loadContractMergeData(reservationId);
  if (!mergeData) throw new Error('Réservation introuvable');

  // 1. Explicit template requested
  let template = null as any;
  if (opts?.templateId) {
    const { data } = await supabase
      .from('contract_templates')
      .select('id, content')
      .eq('id', opts.templateId)
      .maybeSingle();
    template = data;
  }

  // 2. Otherwise pick the default template for this contract type
  if (!template) {
    const { data } = await supabase
      .from('contract_templates')
      .select('id, content')
      .eq('contract_type', contractType)
      .eq('is_default', true)
      .maybeSingle();
    template = data;
  }

  if (template?.content) {
    return {
      content: applyContractMergeFields(template.content, mergeData),
      templateId: template.id,
    };
  }

  // Fallback: legacy hard-coded generator
  return {
    content: generateContractContent(
      mergeData.reservation as Reservation,
      contractType,
      mergeData.client || undefined
    ),
    templateId: null,
  };
};

/**
 * Re-apply merge fields on an existing contract WITHOUT changing its template.
 * Used after the client fills in their address on the public link.
 */
export const regenerateContractWithAddress = async (contractId: string): Promise<void> => {
  const { data: contract, error } = await supabase
    .from('contracts')
    .select('id, reservation_id, contract_type, template_id, status')
    .eq('id', contractId)
    .single();
  if (error) throw error;
  if (contract.status === 'signed') return;

  const mergeData = await loadContractMergeData(contract.reservation_id);
  if (!mergeData) throw new Error('Réservation introuvable');

  let html: string;
  if (contract.template_id) {
    const { data: template } = await supabase
      .from('contract_templates')
      .select('content')
      .eq('id', contract.template_id)
      .maybeSingle();
    if (template?.content) {
      html = applyContractMergeFields(template.content, mergeData);
    } else {
      html = generateContractContent(
        mergeData.reservation as Reservation,
        contract.contract_type as 'lease' | 'rules',
        mergeData.client || undefined
      );
    }
  } else {
    html = generateContractContent(
      mergeData.reservation as Reservation,
      contract.contract_type as 'lease' | 'rules',
      mergeData.client || undefined
    );
  }

  const { error: updErr } = await supabase
    .from('contracts')
    .update({ content: html })
    .eq('id', contractId);
  if (updErr) throw updErr;
};
